import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Menu, X, Search } from "lucide-react";
import { useState, KeyboardEvent } from "react";
import { navigationRoutes } from '../lib/router';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { SearchResults } from './SearchResults';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const location = useLocation();
  const navigate = useNavigate();
  const { results, loading } = useUnifiedSearch(searchQuery);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prevIndex) =>
        prevIndex < results.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
    } else if (e.key === 'Enter' && selectedIndex !== -1) {
      e.preventDefault();
      const selectedResult = results[selectedIndex];
      if (selectedResult) {
        navigate(selectedResult.url);
        setSearchQuery('');
        setShowSearchResults(false);
      }
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-primary/20 glass-card">
      <div className="container flex h-16 items-center">
        {/* Logo - Always visible */}
        <Link className="mr-4 flex items-center space-x-2" to="/">
          <img 
            src="/logo.png" 
            alt="Prompt Literacy.ai" 
            className="h-10 sm:h-12 w-auto"
          />
        </Link>
        
        {/* Desktop Navigation - Hidden on smaller screens */}
        <nav className="hidden lg:flex items-center space-x-6 text-sm font-medium mr-4">
          {navigationRoutes.slice(1).map((route) => (
            <Link
              key={route.path}
              className={`link-underline whitespace-nowrap ${
                location.pathname === route.path 
                  ? 'text-primary active' 
                  : 'text-foreground/60'
              }`}
              to={route.path}
            >
              {route.label}
            </Link>
          ))}
        </nav>
        
        {/* Mobile menu button */}
        <button
          className="inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground h-9 py-2 mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          <span className="sr-only">Toggle Menu</span>
        </button>
        
        {/* Right side content */}
        <div className="flex flex-1 items-center justify-end space-x-2">
          {/* Search - Progressive disclosure */}
          <div className="hidden md:block relative">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search news and examples..."
                className="pl-8 w-48 lg:w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                onKeyDown={handleKeyDown}
              />
            </div>
            {showSearchResults && (searchQuery.length > 1 || loading) && (
              <div className="absolute top-full left-0 right-0 z-[9999]">
                <SearchResults 
                  results={results} 
                  loading={loading} 
                  query={searchQuery}
                  selectedIndex={selectedIndex}
                />
              </div>
            )}
          </div>
          
          {/* Action buttons - Progressive disclosure */}
          <div className="hidden lg:flex items-center space-x-2">
            <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors hidden xl:flex">
              <Link to="/what-is-prompt-literacy">What is Prompt Literacy?</Link>
            </Button>
            <Button variant="default" size="sm" asChild className="btn-tech">
              <Link to="/news">News</Link>
            </Button>
          </div>
          
          {/* Mobile-only actions */}
          <div className="flex md:hidden items-center space-x-2">
            <Button variant="default" size="sm" asChild className="btn-tech">
              <Link to="/news">News</Link>
            </Button>
          </div>
        </div>
      </div>
      
      {isMenuOpen && (
        <div className="lg:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-border/40">
            {/* Mobile Search */}
            <div className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              {searchQuery.length > 1 && (
                <div className="mt-2">
                  <SearchResults 
                    results={results} 
                    loading={loading} 
                    query={searchQuery}
                    selectedIndex={selectedIndex}
                  />
                </div>
              )}
            </div>
            
            {navigationRoutes.slice(1).map((route) => (
              <Link
                key={route.path}
                className={`block px-3 py-2 text-base font-medium link-underline ${
                  location.pathname === route.path 
                    ? 'text-primary active' 
                    : 'text-foreground/60'
                }`}
                to={route.path}
                onClick={() => setIsMenuOpen(false)}
              >
                {route.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}